/**
 * useStateHistory - Shared state history composable for DevPanel
 * 
 * Provides a singleton state history that can be shared between
 * the StateTab and the detachable StateHistoryPopover.
 */
import { ref, watch } from "vue";

// =============================================================================
// Types
// =============================================================================

export interface HistoryEntry {
  id: number;
  timestamp: number;
  source: string;
  key: string;
  oldValue: unknown;
  newValue: unknown;
}

interface PopoverPosition {
  x: number;
  y: number;
}

// =============================================================================
// Storage Keys
// =============================================================================

const STORAGE_KEY = "ai-assistant-state-history";
const DETACHED_KEY = "ai-assistant-state-history-detached";
const POSITION_KEY = "ai-assistant-state-history-position";

// =============================================================================
// Singleton State
// =============================================================================

const MAX_HISTORY = 50;
let historyIdCounter = 0;

const stateHistory = ref<HistoryEntry[]>([]);
const isDetached = ref<boolean>(loadDetachedState());
const popoverPosition = ref<PopoverPosition>(loadPopoverPosition());
const showHistory = ref(false);

// =============================================================================
// Persistence
// =============================================================================

function loadDetachedState(): boolean {
  try {
    const stored = localStorage.getItem(DETACHED_KEY);
    return stored === "true";
  } catch {
    return false;
  }
}

function saveDetachedState(value: boolean): void {
  try {
    localStorage.setItem(DETACHED_KEY, String(value));
  } catch {
    // Ignore storage errors
  }
}

function loadPopoverPosition(): PopoverPosition {
  try {
    const stored = localStorage.getItem(POSITION_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // Ignore parse errors
  }
  return { x: -1, y: -1 }; // -1 means use default position
}

function savePopoverPosition(pos: PopoverPosition): void {
  try {
    localStorage.setItem(POSITION_KEY, JSON.stringify(pos));
  } catch {
    // Ignore storage errors
  }
}

// Watch and persist detached state
watch(isDetached, (value) => {
  saveDetachedState(value);
});

// Watch and persist popover position
watch(popoverPosition, (value) => {
  savePopoverPosition(value);
}, { deep: true });

// =============================================================================
// Actions
// =============================================================================

function addHistoryEntry(source: string, key: string, oldValue: unknown, newValue: unknown): void {
  // Don't add if values are the same
  if (JSON.stringify(oldValue) === JSON.stringify(newValue)) return;

  stateHistory.value.push({
    id: ++historyIdCounter,
    timestamp: Date.now(),
    source,
    key,
    oldValue,
    newValue,
  });

  // Keep only recent entries
  if (stateHistory.value.length > MAX_HISTORY) {
    stateHistory.value = stateHistory.value.slice(-MAX_HISTORY);
  }
}

function clearHistory(): void {
  stateHistory.value = [];
}

function toggleDetach(): void {
  isDetached.value = !isDetached.value;
  // Auto-show history when detaching
  if (isDetached.value) {
    showHistory.value = true;
  }
}

function attach(): void {
  isDetached.value = false;
}

function detach(): void {
  isDetached.value = true;
  showHistory.value = true;
}

function updatePopoverPosition(x: number, y: number): void {
  popoverPosition.value = { x, y };
}

function resetPopoverPosition(): void {
  popoverPosition.value = { x: -1, y: -1 };
}

// =============================================================================
// Formatters
// =============================================================================

function formatHistoryTime(timestamp: number): string {
  return new Date(timestamp).toLocaleTimeString("en-US", {
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function formatValue(value: unknown): string {
  if (value === null) return "null";
  if (value === undefined) return "undefined";
  if (typeof value === "object") {
    try {
      const str = JSON.stringify(value);
      return str.length > 30 ? str.slice(0, 30) + "..." : str;
    } catch {
      return "[object]";
    }
  }
  return String(value);
}

// =============================================================================
// Composable Export
// =============================================================================

export function useStateHistory() {
  return {
    // State
    stateHistory,
    isDetached,
    popoverPosition,
    showHistory,
    
    // Actions
    addHistoryEntry,
    clearHistory,
    toggleDetach,
    attach,
    detach,
    updatePopoverPosition,
    resetPopoverPosition,
    
    // Formatters
    formatHistoryTime,
    formatValue,
  };
}

